import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LdsAlert,
  LdsBox,
  LdsButton,
  LdsDialog,
  LdsFormField,
  LdsOption,
  LdsPaper,
  LdsProgressCircular,
  LdsSelect,
  LdsTab,
  LdsTabPanel,
  LdsTable,
  LdsTabs,
  LdsTabsBar,
  LdsTextField,
} from '@lds/react';
import { AxiosInstance } from 'axios';
import { DateRangePicker } from './DateRangePicker';
import { parseTab, TAB_TO_LOG_TYPE } from './canonicalLog';
import type { LogTab } from './canonicalLog';
import styles from './LogsPage.module.scss';
import './LogsPage.global.scss';

export interface LogEntry {
  timestamp: string;
  executionId?: string;
  status?: string;
  executionMode?: string;
  durationMs?: number;
  evaluatedCount?: number;
  affectedCount?: number;
  action?: string;
  accountId?: string;
  operation?: string;
  operatorName?: string;
  outcome?: string;
  guardrail?: string;
  source?: string;
  message?: string;
  raw: Record<string, unknown>;
}

interface ExecutionPageResponse {
  items: Array<Record<string, unknown>>;
  totals?: {
    total?: number;
    completed?: number;
    failed?: number;
    running?: number;
    partial?: number;
  };
  next_cursor?: string | null;
}

interface LogsResponse {
  logs: Array<Record<string, unknown>>;
}

export interface LogsPageProps {
  apiClient: AxiosInstance;
}

type PeriodPreset = '12h' | '1d' | '7d' | '30d' | '3m' | 'custom';

const VALID_PERIODS: ReadonlySet<PeriodPreset> = new Set<PeriodPreset>(['12h', '1d', '7d', '30d', '3m', 'custom']);

function computePeriodDates(preset: PeriodPreset): { from: string; to: string } {
  const end = new Date();
  const to = end.toISOString();
  let start: Date;
  switch (preset) {
    case '12h':
      start = new Date(end.getTime() - 12 * 60 * 60 * 1000);
      break;
    case '1d':
      start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7d':
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '3m':
      start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    default:
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
  }
  return { from: start.toISOString(), to };
}

function parsePeriod(value: string | null): PeriodPreset {
  if (value && VALID_PERIODS.has(value as PeriodPreset)) {
    return value as PeriodPreset;
  }
  return '7d';
}

function toNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function LogsPage({ apiClient }: LogsPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: LogTab = parseTab(searchParams.get('tab'));

  const initialPeriod = parsePeriod(searchParams.get('period'));
  const initialDates = computePeriodDates(initialPeriod);
  const initialFromParam = searchParams.get('from');
  const initialToParam = searchParams.get('to');
  const useUrlDates = initialPeriod === 'custom' && !!initialFromParam && !!initialToParam;

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodPreset>(initialPeriod);
  const [from, setFrom] = useState(useUrlDates ? (initialFromParam as string) : initialDates.from);
  const [to, setTo] = useState(useUrlDates ? (initialToParam as string) : initialDates.to);
  const [accountId, setAccountId] = useState(searchParams.get('account_id') || '');
  const [guardrail, setGuardrail] = useState(searchParams.get('guardrail') || '');
  const [action, setAction] = useState(searchParams.get('action') || '');
  const [resourceSearch, setResourceSearch] = useState(searchParams.get('q') || '');
  const [executionStatus, setExecutionStatus] = useState(searchParams.get('status') || '');
  const [executionMode, setExecutionMode] = useState(searchParams.get('execution_mode') || '');
  const [decision, setDecision] = useState(searchParams.get('decision') || '');

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [executionTotals, setExecutionTotals] = useState<ExecutionPageResponse['totals'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [guardrailOptions, setGuardrailOptions] = useState<string[]>([]);
  const [timezone, setTimezone] = useState<'utc' | 'local'>('local');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  const initialFetchDone = useRef(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ guardrails: string[] }>('/guardrails')
      .then((response) => {
        if (cancelled) return;
        const options = response.data.guardrails ?? [];
        setGuardrailOptions(options.length ? options : ['iam-accesskeys']);
      })
      .catch(() => {
        if (cancelled) return;
        setGuardrailOptions(['iam-accesskeys']);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; clear: () => void }> = [];
    if (accountId) chips.push({ key: 'account_id', label: `Conta: ${accountId}`, clear: () => setAccountId('') });
    if (guardrail) chips.push({ key: 'guardrail', label: `Guardrail: ${guardrail}`, clear: () => setGuardrail('') });
    if (action) chips.push({ key: 'action', label: `Ação: ${action}`, clear: () => setAction('') });
    if (resourceSearch) chips.push({ key: 'q', label: `Busca: ${resourceSearch}`, clear: () => setResourceSearch('') });
    if (executionStatus) chips.push({ key: 'status', label: `Status: ${executionStatus}`, clear: () => setExecutionStatus('') });
    if (executionMode) chips.push({ key: 'execution_mode', label: `Modo: ${executionMode}`, clear: () => setExecutionMode('') });
    if (decision) chips.push({ key: 'decision', label: `Decisão: ${decision}`, clear: () => setDecision('') });
    return chips;
  }, [accountId, action, decision, executionMode, executionStatus, guardrail, resourceSearch]);

  const syncUrlState = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', activeTab);
    params.set('period', selectedPeriod);
    params.set('from', from);
    params.set('to', to);

    const optional: Record<string, string> = {
      account_id: accountId,
      guardrail,
      action,
      q: resourceSearch,
      status: executionStatus,
      execution_mode: executionMode,
      decision,
    };

    Object.entries(optional).forEach(([key, value]) => {
      if (value && value.trim() !== '') params.set(key, value.trim());
      else params.delete(key);
    });

    setSearchParams(params);
  }, [activeTab, accountId, action, decision, executionMode, executionStatus, from, guardrail, resourceSearch, searchParams, selectedPeriod, setSearchParams, to]);

  const fetchLogs = useCallback(async (cursor?: string, append = false) => {
    if (!from || !to) return;
    if (append) setLoadingMore(true);
    else setLoading(true);

    setError(null);
    setSearched(true);

    try {
      if (activeTab === 'execucoes') {
        const params: Record<string, string> = { from, to, limit: '50' };
        if (accountId) params.account_id = accountId;
        if (guardrail) params.guardrail = guardrail;
        if (action) params.action = action;
        if (resourceSearch) params.resource_search = resourceSearch;
        if (executionStatus) params.status = executionStatus;
        if (executionMode) params.execution_mode = executionMode;
        if (decision) params.decision = decision;
        if (cursor) params.cursor = cursor;

        const response = await apiClient.get<ExecutionPageResponse>('/logs/executions', { params });
        const page = response.data;
        const entries: LogEntry[] = (page.items ?? []).map((item) => ({
          timestamp: String(item.started_at ?? item.startedAt ?? ''),
          executionId: String(item.execution_id ?? item.executionId ?? ''),
          status: String(item.status ?? ''),
          executionMode: String(item.execution_mode ?? item.executionMode ?? ''),
          durationMs: toNumber(item.duration_ms ?? item.durationMs),
          evaluatedCount: toNumber(item.evaluated_count ?? item.evaluatedCount),
          affectedCount: toNumber(item.affected_count ?? item.affectedCount),
          accountId: String(item.account_id ?? item.accountId ?? ''),
          guardrail: String(item.guardrail ?? ''),
          raw: item,
        }));

        setExecutionTotals(page.totals ?? null);
        setNextCursor(page.next_cursor ?? null);
        setLogs((current) => (append ? [...current, ...entries] : entries));
      } else {
        const params: Record<string, string> = { from, to, log_type: TAB_TO_LOG_TYPE[activeTab] };
        if (accountId) params.account_id = accountId;
        if (guardrail) params.guardrail = guardrail;
        if (action) params.action = action;

        const response = await apiClient.get<LogsResponse>('/logs', { params });
        const rawEntries = response.data.logs ?? [];
        const entries: LogEntry[] = rawEntries.map((entry) => {
          const raw = (entry.raw as Record<string, unknown>) ?? entry;
          return {
            timestamp: String(entry.timestamp ?? raw['@timestamp'] ?? ''),
            action: String(entry.action ?? raw.action ?? ''),
            accountId: String(entry.accountId ?? entry.account_id ?? raw.account_id ?? ''),
            operation: String(entry.operation ?? raw.operation ?? ''),
            operatorName: String(entry.operatorName ?? raw.operator_name ?? ''),
            outcome: String(entry.outcome ?? raw.outcome ?? ''),
            guardrail: String(entry.guardrail ?? raw.guardrail ?? ''),
            source: String(entry.source ?? raw.component ?? raw.guardrail ?? ''),
            message: String(entry.message ?? raw.message ?? ''),
            raw,
          };
        });

        setNextCursor(null);
        setExecutionTotals(null);
        setLogs(entries);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha ao carregar logs';
      setError(message);
      if (!append) {
        setLogs([]);
        setExecutionTotals(null);
        setNextCursor(null);
      }
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [accountId, action, activeTab, apiClient, decision, executionMode, executionStatus, from, guardrail, resourceSearch, to]);

  useEffect(() => {
    if (!initialFetchDone.current && from && to) {
      initialFetchDone.current = true;
      fetchLogs();
    }
  }, [fetchLogs, from, to]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedLog(null);
      }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  const handleTabChange = useCallback((_event: unknown, newValue: string | number | null) => {
    if (newValue === null) return;
    const parsed = parseTab(String(newValue));
    const next = new URLSearchParams(searchParams);
    next.set('tab', parsed);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handlePeriodChange = (preset: PeriodPreset) => {
    setSelectedPeriod(preset);
    if (preset === 'custom') {
      setShowCalendar(true);
      return;
    }
    const period = computePeriodDates(preset);
    setFrom(period.from);
    setTo(period.to);
    setShowCalendar(false);
  };

  const handleSearch = async () => {
    syncUrlState();
    await fetchLogs(undefined, false);
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore || activeTab !== 'execucoes') return;
    await fetchLogs(nextCursor, true);
  };

  const clearFilters = () => {
    setAccountId('');
    setGuardrail('');
    setAction('');
    setResourceSearch('');
    setExecutionStatus('');
    setExecutionMode('');
    setDecision('');
  };

  async function handleRowClick(entry: LogEntry) {
    if (activeTab !== 'execucoes' || !entry.executionId) {
      setSelectedLog(entry);
      return;
    }

    try {
      const response = await apiClient.get<Record<string, unknown>>(
        `/logs/executions/${encodeURIComponent(entry.executionId)}`,
        { params: { from, to } },
      );
      setSelectedLog({ ...entry, raw: response.data });
    } catch {
      setSelectedLog(entry);
    }
  }

  function formatTimestamp(ts: string): string {
    if (!ts) return '—';
    try {
      let normalized = ts.trim();
      if (!/[Z+]/.test(normalized.slice(10)) && !normalized.slice(10).includes('-')) {
        normalized = normalized.replace(' ', 'T') + 'Z';
      }
      const d = new Date(normalized);
      if (Number.isNaN(d.getTime())) return ts;

      const pad = (n: number, len = 2) => String(n).padStart(len, '0');
      if (timezone === 'utc') {
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)} UTC`;
      }

      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
    } catch {
      return ts;
    }
  }

  function renderExecutionStatus(status?: string) {
    const normalized = (status || 'partial').toLowerCase();
    const cssClass =
      normalized === 'completed'
        ? styles.statusCompleted
        : normalized === 'failed'
        ? styles.statusFailed
        : normalized === 'running'
        ? styles.statusRunning
        : styles.statusPartial;
    return <span className={`${styles.statusPill} ${cssClass}`}>{normalized}</span>;
  }

  function renderContent() {
    if (loading) {
      return (
        <LdsBox className={styles.centered} data-testid="logs-loading">
          <LdsProgressCircular indeterminate animated ariaLabel="Carregando" />
        </LdsBox>
      );
    }

    if (error) {
      return <LdsAlert color="critical" description={error} data-testid="logs-error" />;
    }

    if (searched && logs.length === 0) {
      return (
        <LdsAlert
          color="info"
          description="Nenhuma entrada encontrada para os filtros selecionados. Ajuste período/filtros e tente novamente."
          data-testid="logs-empty"
        />
      );
    }

    if (logs.length === 0) {
      return null;
    }

    return (
      <>
        {activeTab === 'execucoes' && executionTotals && (
          <LdsBox className={styles.executionTotals} data-testid="execution-totals">
            <span>Total: {executionTotals.total ?? 0}</span>
            <span>Concluídas: {executionTotals.completed ?? 0}</span>
            <span>Falhas: {executionTotals.failed ?? 0}</span>
            <span>Em execução: {executionTotals.running ?? 0}</span>
            <span>Parciais: {executionTotals.partial ?? 0}</span>
          </LdsBox>
        )}

        <LdsPaper elevated>
          <LdsTable aria-label="Logs table" data-testid="logs-table" striped hovered>
            <thead>
              <tr>
                <th>Início {activeTab !== 'execucoes' && timezone === 'utc' ? '(UTC)' : ''}</th>
                {activeTab === 'execucoes' ? (
                  <>
                    <th>Status</th>
                    <th>Account ID</th>
                    <th>Guardrail</th>
                    <th>Modo</th>
                    <th>Recursos Avaliados</th>
                    <th>Recursos Afetados</th>
                    <th>Duração</th>
                  </>
                ) : activeTab === 'auditoria' ? (
                  <>
                    <th>Operação</th>
                    <th>Operador</th>
                    <th>Resultado</th>
                    <th>Guardrail</th>
                  </>
                ) : (
                  <>
                    <th>Fonte</th>
                    <th>Mensagem</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {logs.map((entry, index) => (
                <tr
                  key={`${entry.executionId || 'row'}-${index}`}
                  className={styles.clickableRow}
                  onClick={() => handleRowClick(entry)}
                  data-testid={`log-row-${index}`}
                >
                  <td>{formatTimestamp(entry.timestamp)}</td>
                  {activeTab === 'execucoes' ? (
                    <>
                      <td>{renderExecutionStatus(entry.status)}</td>
                      <td>{entry.accountId || '—'}</td>
                      <td>{entry.guardrail || '—'}</td>
                      <td>{entry.executionMode || '—'}</td>
                      <td>{entry.evaluatedCount ?? '—'}</td>
                      <td>{entry.affectedCount ?? '—'}</td>
                      <td>{entry.durationMs ? `${entry.durationMs} ms` : '—'}</td>
                    </>
                  ) : activeTab === 'auditoria' ? (
                    <>
                      <td>{entry.operation || '—'}</td>
                      <td>{entry.operatorName || '—'}</td>
                      <td>{entry.outcome || '—'}</td>
                      <td>{entry.guardrail || '—'}</td>
                    </>
                  ) : (
                    <>
                      <td>{entry.source || entry.guardrail || '—'}</td>
                      <td>{entry.message || '—'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </LdsTable>
        </LdsPaper>

        {activeTab === 'execucoes' && nextCursor && (
          <LdsBox className={styles.loadMoreWrap}>
            <LdsButton variant="outlined" disabled={loadingMore} onClick={handleLoadMore}>
              {loadingMore ? 'Carregando...' : 'Carregar mais'}
            </LdsButton>
          </LdsBox>
        )}
      </>
    );
  }

  return (
    <LdsBox data-testid="logs-page">
      <LdsTabs value={activeTab} onChange={handleTabChange} data-testid="logs-tabs">
        <LdsBox className={styles.tabsHeader}>
          <LdsTabsBar>
            <LdsTab value="execucoes" data-testid="tab-execucoes">Execuções</LdsTab>
            <LdsTab value="auditoria" data-testid="tab-auditoria">Auditoria</LdsTab>
            <LdsTab value="erros" data-testid="tab-erros">Erros</LdsTab>
          </LdsTabsBar>

          <LdsBox className={styles.tabsActions}>
            <LdsBox className={styles.periodWrapper}>
              <LdsSelect
                value={selectedPeriod}
                onChange={(_event: unknown, value: unknown) => handlePeriodChange(String(value || '7d') as PeriodPreset)}
                aria-label="Período"
                data-field="period-select"
              >
                <LdsOption value="12h">12 horas</LdsOption>
                <LdsOption value="1d">1 dia</LdsOption>
                <LdsOption value="7d">7 dias</LdsOption>
                <LdsOption value="30d">30 dias</LdsOption>
                <LdsOption value="3m">3 meses</LdsOption>
                <LdsOption value="custom">Personalizado</LdsOption>
              </LdsSelect>

              {showCalendar && (
                <DateRangePicker
                  from={from}
                  to={to}
                  onApply={(newFrom, newTo) => {
                    setFrom(newFrom);
                    setTo(newTo);
                    setShowCalendar(false);
                  }}
                  onClose={() => setShowCalendar(false)}
                />
              )}
            </LdsBox>

            <LdsButton variant="basic" onClick={handleSearch} disabled={loading}>
              Atualizar
            </LdsButton>
            <LdsButton variant="basic" onClick={() => setFiltersVisible((value) => !value)}>
              {filtersVisible ? 'Ocultar filtros' : 'Filtros'}
            </LdsButton>
          </LdsBox>
        </LdsBox>

        {filtersVisible && (
          <LdsPaper elevated className={styles.filters} id="logs-filters-panel">
            <LdsBox className={styles.filtersGrid}>
              <LdsBox className={styles.fieldWrap}>
                <LdsFormField label="Conta, recurso ou execução" fieldId="logs-filter-q">
                  <LdsTextField
                    value={resourceSearch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResourceSearch(e.target.value)}
                    aria-label="Filter by account resource or execution"
                  />
                </LdsFormField>
              </LdsBox>

              <LdsBox className={styles.fieldWrap}>
                <LdsFormField label="Account ID" fieldId="logs-filter-account">
                  <LdsTextField
                    value={accountId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountId(e.target.value)}
                    aria-label="Filter by account ID"
                  />
                </LdsFormField>
              </LdsBox>

              <LdsBox className={styles.fieldWrap}>
                <LdsFormField label="Guardrail" fieldId="logs-filter-guardrail">
                  <LdsSelect
                    value={guardrail}
                    onChange={(_event: unknown, value: unknown) => setGuardrail(String(value ?? ''))}
                    aria-label="Filter by guardrail"
                  >
                    <LdsOption value="">All</LdsOption>
                    {guardrailOptions.map((option) => (
                      <LdsOption key={option} value={option}>{option}</LdsOption>
                    ))}
                  </LdsSelect>
                </LdsFormField>
              </LdsBox>

              <LdsBox className={styles.fieldWrap}>
                <LdsFormField label="Action" fieldId="logs-filter-action">
                  <LdsTextField
                    value={action}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAction(e.target.value)}
                    aria-label="Filter by action"
                  />
                </LdsFormField>
              </LdsBox>

              {activeTab === 'execucoes' && (
                <>
                  <LdsBox className={styles.fieldWrap}>
                    <LdsFormField label="Status" fieldId="logs-filter-status">
                      <LdsSelect
                        value={executionStatus}
                        onChange={(_event: unknown, value: unknown) => setExecutionStatus(String(value ?? ''))}
                        aria-label="Filter by execution status"
                      >
                        <LdsOption value="">All</LdsOption>
                        <LdsOption value="completed">completed</LdsOption>
                        <LdsOption value="failed">failed</LdsOption>
                        <LdsOption value="running">running</LdsOption>
                        <LdsOption value="partial">partial</LdsOption>
                      </LdsSelect>
                    </LdsFormField>
                  </LdsBox>

                  <LdsBox className={styles.fieldWrap}>
                    <LdsFormField label="Modo" fieldId="logs-filter-mode">
                      <LdsSelect
                        value={executionMode}
                        onChange={(_event: unknown, value: unknown) => setExecutionMode(String(value ?? ''))}
                        aria-label="Filter by execution mode"
                      >
                        <LdsOption value="">All</LdsOption>
                        <LdsOption value="observe">observe</LdsOption>
                        <LdsOption value="enforce">enforce</LdsOption>
                      </LdsSelect>
                    </LdsFormField>
                  </LdsBox>

                  <LdsBox className={styles.fieldWrap}>
                    <LdsFormField label="Decisão" fieldId="logs-filter-decision">
                      <LdsSelect
                        value={decision}
                        onChange={(_event: unknown, value: unknown) => setDecision(String(value ?? ''))}
                        aria-label="Filter by decision"
                      >
                        <LdsOption value="">All</LdsOption>
                        <LdsOption value="disable">disable</LdsOption>
                        <LdsOption value="keep">keep</LdsOption>
                        <LdsOption value="extend">extend</LdsOption>
                        <LdsOption value="skip">skip</LdsOption>
                      </LdsSelect>
                    </LdsFormField>
                  </LdsBox>
                </>
              )}
            </LdsBox>

            <LdsBox className={styles.filterActions}>
              <LdsButton className={styles.compactButton} variant="contained" onClick={handleSearch} disabled={loading}>
                Buscar
              </LdsButton>
              <LdsButton className={styles.compactButton} variant="outlined" onClick={clearFilters}>
                Limpar
              </LdsButton>
              <LdsButton className={styles.compactButton} variant="basic" onClick={() => setTimezone((value) => (value === 'utc' ? 'local' : 'utc'))}>
                Horário: {timezone === 'utc' ? 'UTC' : 'Local'}
              </LdsButton>
            </LdsBox>

            {activeFilterChips.length > 0 && (
              <LdsBox className={styles.chipsRow}>
                {activeFilterChips.map((chip) => (
                  <button key={chip.key} className={styles.chip} onClick={chip.clear} type="button">
                    {chip.label} ×
                  </button>
                ))}
              </LdsBox>
            )}
          </LdsPaper>
        )}

        <LdsTabPanel value="execucoes" data-testid="panel-execucoes">{renderContent()}</LdsTabPanel>
        <LdsTabPanel value="auditoria" data-testid="panel-auditoria">{renderContent()}</LdsTabPanel>
        <LdsTabPanel value="erros" data-testid="panel-erros">{renderContent()}</LdsTabPanel>
      </LdsTabs>

      {selectedLog && selectedLog.executionId && (
        <div className={styles.sidePanelBackdrop} onClick={() => setSelectedLog(null)}>
          <aside className={styles.sidePanel} onClick={(e) => e.stopPropagation()}>
            <header className={styles.sidePanelHeader}>
              <h3>Execução {selectedLog.executionId}</h3>
              <button className={styles.closeBtn} type="button" onClick={() => setSelectedLog(null)}>Fechar</button>
            </header>
            <div className={styles.sidePanelBody}>
              <pre className={styles.json} data-testid="execution-detail-json">{JSON.stringify(selectedLog.raw, null, 2)}</pre>
            </div>
          </aside>
        </div>
      )}

      {selectedLog && !selectedLog.executionId && (
        <LdsDialog
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Detalhe da entrada de log"
          size="lg"
          closeButtonAriaLabel="Fechar"
          data-testid="log-detail-dialog"
          footer={<LdsButton variant="outlined" onClick={() => setSelectedLog(null)}>Fechar</LdsButton>}
        >
          <pre className={styles.json} data-testid="log-detail-json">{JSON.stringify(selectedLog.raw, null, 2)}</pre>
        </LdsDialog>
      )}
    </LdsBox>
  );
}
